/**
 * Auth error handling utilities
 *
 * Extracts user-friendly messages from various error types (ApiError,
 * network errors, timeouts, DOMException) for display in the UI.
 */

import type { ApiError } from "@/services/shared/infrastructure";

/**
 * Extract a user-friendly error message from any error thrown during auth flows.
 * Handles ApiError, DOMException (abort/timeout), network errors, and generic Errors.
 */
export function toAuthErrorMessage(error: unknown): string {
  if (error == null) return "An unexpected error occurred";

  // ApiError from api-client (has message and statusCode)
  const apiErr = error as ApiError;
  if (apiErr.message && typeof apiErr.message === "string") {
    // Map common status codes to friendlier messages
    if (apiErr.statusCode === 401) {
      return (
        apiErr.message ||
        "Invalid email or password. Please check your credentials and try again."
      );
    }
    if (apiErr.statusCode === 400 || apiErr.statusCode === 422) {
      return apiErr.message || "Invalid request. Please check your input.";
    }
    if (apiErr.statusCode === 409) {
      return (
        apiErr.message ||
        "An account with this email already exists. Please log in instead."
      );
    }
    if (apiErr.statusCode === 404) {
      return "Authentication service unavailable. Please try again later.";
    }
    if (
      apiErr.statusCode === 500 ||
      apiErr.statusCode === 502 ||
      apiErr.statusCode === 503
    ) {
      return apiErr.message || "Server error. Please try again later.";
    }
    return apiErr.message;
  }

  // DOMException (AbortError, TimeoutError)
  if (error instanceof DOMException) {
    if (error.name === "TimeoutError") {
      return "Request timed out. Please check your connection and try again.";
    }
    if (error.name === "AbortError") {
      return "Request was cancelled. Please try again.";
    }
    return error.message || "An unexpected error occurred";
  }

  // Standard Error
  if (error instanceof Error) {
    const msg = error.message?.trim();
    if (
      msg?.toLowerCase().includes("failed to fetch") ||
      msg?.toLowerCase().includes("networkerror")
    ) {
      return "Unable to connect. Please check your internet connection and that the server is running.";
    }
    if (
      msg?.toLowerCase().includes("timeout") ||
      msg?.toLowerCase().includes("timed out")
    ) {
      return "Request timed out. Please try again.";
    }
    if (msg?.toLowerCase().includes("cors")) {
      return "Connection blocked. Please ensure the API server is running and CORS is configured correctly.";
    }
    return msg || "An unexpected error occurred";
  }

  // Object with message/error
  if (typeof error === "object") {
    const obj = error as Record<string, unknown>;
    const msg = obj.message ?? obj.error;
    if (typeof msg === "string" && msg.trim()) return msg;
  }

  return "An unexpected error occurred. Please try again.";
}
